'use client';

import { DocumentNode, useQuery } from '@apollo/client';
import { FC } from 'react';
import { Controller, useFormContext, ValidationRule } from 'react-hook-form';

import { ClickableChip } from '@/components/ui/ClickableChip/ClickableChip';
import { TypographyVariant } from '@/components/ui/Typography/Typography.types';
import { EMPTY_QUERY } from '@/graphql/queries/emptyQuery';

import { getChipData, getSelectedItems } from './ChipFilters.helpers';
import * as S from './ChipsFilter.styles';

type ChipType = {
  id: string;
  name: string;
};

type ChipsFilterProps = {
  options?: ChipType[];
  query?: DocumentNode;
  fieldName: string;
  formField: string;
  multiple?: boolean;
  className?: string;
  titleVariant?: TypographyVariant;
  rules?: Record<string, ValidationRule>;
  dataCyOption?: (id: string) => string;
};

type FormData = {
  [key: string]: ChipType[] | ChipType | null;
};

export const ChipsFilter: FC<ChipsFilterProps> = ({
  options,
  query,
  fieldName,
  formField,
  multiple = true,
  className,
  titleVariant,
  rules,
  dataCyOption
}) => {
  const isArray = Array.isArray(options);
  const { data, error } = useQuery(query ?? EMPTY_QUERY, {
    skip: !query || isArray
  });

  const { control, trigger } = useFormContext<FormData>();

  const isInvalidData = error && data && !(fieldName in data);
  if (!options?.length && isInvalidData) return null;

  return (
    <Controller
      name={formField}
      rules={rules}
      control={control}
      render={({ field: { value, onChange, ...rest } }) => {
        const selectedItems = getSelectedItems(value, multiple);

        const dataOptions = isArray ? options : getChipData(data, fieldName);

        const handleChange = (item: ChipType) => {
          const isSelected = selectedItems.some(i => i.id === item.id);
          if (multiple) {
            const updatedItems = isSelected
              ? selectedItems.filter(i => i.id !== item.id)
              : [...selectedItems, item];
            onChange(updatedItems);
          } else {
            onChange(isSelected ? null : item);
          }
          trigger(formField);
        };

        return (
          <S.ChipsWrapper>
            {dataOptions.map(item => {
              const isSelected = selectedItems.some(i => i.id === item.id);
              return (
                <ClickableChip
                  {...rest}
                  titleVariant={titleVariant}
                  className={className}
                  id={item.id}
                  key={item.id}
                  label={item.name}
                  isSelected={isSelected}
                  data-testid={`chip-${item.id}`}
                  data-cy={dataCyOption?.(item.id)}
                  onClick={() => handleChange(item)}
                  onBlur={rest.onBlur}
                />
              );
            })}
          </S.ChipsWrapper>
        );
      }}
    />
  );
};
